import React, { useState } from "react";
import { Button } from "../ui/button";
import { ArrowLeft, Edit } from "lucide-react";
import { CedentesTab } from "./CedentesTab";
import { DocumentsTab } from "./DocumentsTab";
import { SignersTab } from "./SignersTab";
import { DashboardTab } from "./DashboardTab";
import { StatusImportDialog } from "./cedentes/StatusImportDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { processService } from "../../services/process.service";
import { Checkbox } from "../ui/checkbox";

const TABS = [
  { key: "cedentes", label: "Substituídos" },
  { key: "documentos", label: "Documentos" },
  { key: "signatarios", label: "Signatários" },
  { key: "dashboard", label: "Dashboard" },
];

export function ProcessDetail({ processo, onBack, onRefresh }) {
  const [activeTab, setActiveTab] = useState("cedentes");
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    nome_processo: processo?.nome_processo || "",
    cnj: processo?.cnj || "",
    vara: processo?.vara || "",
    uf: processo?.uf || "",
    bloquear_cessao: Boolean(processo?.bloquear_cessao),
  });

  if (!processo) return null;

  const handleUpdate = async () => {
    try {
      await processService.updateProcesso(processo.id, editData);
      toast.success("Processo atualizado");
      setIsEditDialogOpen(false);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error("Erro ao atualizar: " + e.message);
    }
  };

  return (
    <div className="p-4 md:p-8 w-full max-w-6xl mx-auto space-y-6">
      <div className="flex justify-between items-center mb-4">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Voltar para Lista
        </Button>
        <Button 
          variant="outline"
          onClick={() => setIsStatusDialogOpen(true)}
          className="gap-2 border-slate-200 text-slate-600"
        >
          <Edit className="w-4 h-4" />
          Atualizar Status dos Substituídos
        </Button>
      </div>
      
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-slate-800">Processo: {processo.nome_processo}</h2>
            <span
              className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                processo.bloquear_cessao
                  ? "bg-amber-100 text-amber-800"
                  : "bg-emerald-100 text-emerald-800"
              }`}
            >
              {processo.bloquear_cessao ? "Cessão bloqueada" : "Cessão liberada"}
            </span>
            <Button variant="ghost" size="icon" onClick={() => setIsEditDialogOpen(true)} className="h-8 w-8">
              <Edit className="h-4 w-4 text-slate-400 hover:text-blue-500" />
            </Button>
          </div>
          {processo.cnj && (
            <p className="text-sm font-mono text-gray-500 mt-1">CNJ: {processo.cnj}</p>
          )}
        </div>
        <div className="text-sm text-gray-500 text-right">
          {processo.vara} - {processo.uf}
        </div>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Processo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome do Processo</Label>
              <Input
                value={editData.nome_processo}
                onChange={e => setEditData({ ...editData, nome_processo: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>CNJ</Label>
              <Input
                value={editData.cnj}
                onChange={e => setEditData({ ...editData, cnj: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Vara</Label>
              <Input
                value={editData.vara}
                onChange={e => setEditData({ ...editData, vara: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>UF</Label>
              <Input
                value={editData.uf}
                onChange={e => setEditData({ ...editData, uf: e.target.value })}
              />
            </div>
            <div className="flex items-start space-x-3 rounded-lg border border-slate-200 p-4">
              <Checkbox
                id="process-detail-bloquear-cessao"
                checked={editData.bloquear_cessao}
                onCheckedChange={(checked) =>
                  setEditData({
                    ...editData,
                    bloquear_cessao: Boolean(checked),
                  })
                }
              />
              <div className="space-y-1">
                <Label htmlFor="process-detail-bloquear-cessao">Bloquear cessão de crédito</Label>
                <p className="text-xs text-slate-500">
                  Remove a proposta do frontend e faz o backend rejeitar o segundo round.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleUpdate}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tab Bar */}
      <div className="border-b border-slate-200 flex gap-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? "border-slate-800 text-slate-800"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "cedentes" && <CedentesTab processoId={processo.id} />}
      {activeTab === "documentos" && <DocumentsTab processo={processo} onRefresh={onRefresh} />}
      {activeTab === "signatarios" && <SignersTab processo={processo} onRefresh={onRefresh} />}
      {activeTab === "dashboard" && <DashboardTab processoId={processo.id} />}

      <StatusImportDialog 
        isOpen={isStatusDialogOpen}
        onOpenChange={setIsStatusDialogOpen}
        onSuccess={onRefresh}
        processoId={processo.id}
      />
    </div>
  );
}
